import React, { FC, ReactNode, useCallback } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared/enum";
import { AnyEntity } from "@mapmycustomers/shared/types/entity";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";

import {
  TabbedDrillDownModal,
  TabbedDrillDownModalProps,
} from "@app/scene/dashboard/components/cards/common/DrillDownModal";
import { NO_CONTACT_IN_ENTITY_TYPES } from "@app/scene/dashboard/components/cards/NoContactIn/consts";
import {
  exportNoContactInCardDrillDownData,
  fetchNoContactInCardDrillDownData,
} from "@app/scene/dashboard/store/cards/noContactIn/actions";
import {
  getNoContactInDrillDownTotalFilteredRecords,
  getNoContactInDrillDownViewStates,
} from "@app/scene/dashboard/store/cards/noContactIn/selectors";
import { TabbedDrillDownListFetcherPayload } from "@app/scene/dashboard/types/TabbedDrillDownListFetcher";
import { showEntityView } from "@app/store/entityView/actions";
import { RootState } from "@app/store/rootReducer";
import type EntityTypesSupportedInNoContactIn from "@app/types/dashboard/cards/noContactIn/EntityTypesSupportedInNoContactIn";
import NoContactInCardConfiguration from "@app/types/dashboard/cards/noContactIn/NoContactInCardConfiguration";
import Scope from "@app/types/dashboard/Scope";

import cardMessages from "../../messages";
import messages from "../messages";

interface Props
  extends Pick<TabbedDrillDownModalProps<EntityTypesSupportedInNoContactIn>, "onEdit" | "onHide"> {
  configuration: NoContactInCardConfiguration;
  onExportDrillDownData: typeof exportNoContactInCardDrillDownData;
  onFetchDrillDownData: (
    payload: TabbedDrillDownListFetcherPayload<AnyEntity, EntityTypesSupportedInNoContactIn>
  ) => void;
  onShowEntityView: typeof showEntityView;
  scope: Scope;
  totalFilteredRecords: Record<EntityTypesSupportedInNoContactIn, number>;
  viewStates: Record<EntityTypesSupportedInNoContactIn, ViewState>;
}

const DrillDown: FC<Props> = ({
  configuration,
  onEdit,
  onExportDrillDownData,
  onFetchDrillDownData,
  onHide,
  onShowEntityView,
  scope,
  totalFilteredRecords,
  viewStates,
}) => {
  const intl = useIntl();

  const handleExport = useCallback(
    (entityType: EntityType) => {
      onExportDrillDownData({
        configuration,
        entityType: entityType as EntityTypesSupportedInNoContactIn,
        scope,
      });
    },
    [onExportDrillDownData, configuration, scope]
  );

  const handleRecordView = useCallback(
    (entity: AnyEntity) => {
      onShowEntityView({
        entityId: entity.id,
        entityType: entity.entity as EntityType.COMPANY | EntityType.DEAL | EntityType.PERSON,
        zIndex: 1001, // to override the drill down modal
      });
    },
    [onShowEntityView]
  );

  return (
    <TabbedDrillDownModal<EntityTypesSupportedInNoContactIn>
      allEntityTypes={NO_CONTACT_IN_ENTITY_TYPES}
      entityTypes={
        configuration.criteria.entityTypes.length
          ? configuration.criteria.entityTypes
          : NO_CONTACT_IN_ENTITY_TYPES
      }
      onEdit={onEdit}
      onExportDrillDownData={handleExport}
      onFetchDrillDownData={onFetchDrillDownData}
      onHide={onHide}
      onRecordView={handleRecordView}
      subtitle={intl.formatMessage(messages.drillDownSubtitle)}
      title={
        configuration.customTitle?.trim() ||
        intl.formatMessage(cardMessages.cardTitle, {
          date: (text: ReactNode) => <>{text}</>,
          days: configuration?.dateRange.days,
          neverContacted: configuration?.dateRange.days === null,
        })
      }
      totalFilteredRecords={totalFilteredRecords}
      viewStates={viewStates}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  totalFilteredRecords: getNoContactInDrillDownTotalFilteredRecords(state),
  viewStates: getNoContactInDrillDownViewStates(state),
});

const mapDispatchToProps = {
  onExportDrillDownData: exportNoContactInCardDrillDownData,
  onFetchDrillDownData: fetchNoContactInCardDrillDownData.request,
  onShowEntityView: showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps)(DrillDown);
